import { useEffect, useState } from 'react'
import { getEmailById, type EmailDetailResponse } from './api'
import type { ChatInboxEmail } from '../../shared/types/chat'

const cache = new Map<string, EmailDetailResponse>()
const inflight = new Map<string, Promise<EmailDetailResponse>>()

function toChatInboxEmail(detail: EmailDetailResponse): ChatInboxEmail {
  return {
    id: detail.id,
    sender: detail.senderName,
    senderEmail: detail.fromEmail,
    subject: detail.subject,
    body: detail.bodyText,
    bodyHtml: detail.bodyHtml,
    verified: true,
    unsubscribe: detail.unsubscribe ?? undefined,
  }
}

/**
 * Lazy-load an inbound email by id. Results are cached for the lifetime of the
 * session so re-renders (and paging through history) don't refetch.
 */
export function useEmailDetail(emailId: string | undefined): ChatInboxEmail | null {
  // Bump a counter whenever a fetch resolves so render reads the new cache entry.
  const [, setTick] = useState(0)

  useEffect(() => {
    if (!emailId || cache.has(emailId)) return
    let cancelled = false
    const existing = inflight.get(emailId)
    const promise = existing ?? getEmailById(emailId).then(detail => {
      cache.set(emailId, detail)
      inflight.delete(emailId)
      return detail
    }).catch(err => {
      inflight.delete(emailId)
      throw err
    })
    if (!existing) inflight.set(emailId, promise)
    promise.then(() => {
      if (!cancelled) setTick(t => t + 1)
    }).catch(() => {
      // Card stays in its loading state; swallow the error.
    })
    return () => { cancelled = true }
  }, [emailId])

  if (!emailId) return null
  const cached = cache.get(emailId)
  return cached ? toChatInboxEmail(cached) : null
}
